import { createContext } from "react";
import type {
  CompanyShellRow,
  CompanyTemplateRef,
  MyPayEnvironment,
} from "../../generated/core-api";
import type {
  CompanyShellCsvColumn,
  CompanyShellCsvParseError,
  CompanyShellValidateResult,
} from "../types";
import type { CompanyShellWorkloadsManagerReturn } from "../hooks";

/**
 * Where a batch came from, when it was extracted from a questionnaire rather
 * than read from a CSV.
 *
 * Lives on the wizard state rather than in the upload screen because the
 * warning has to follow the rows: extraction is non-deterministic and the
 * server validator cannot catch a plausible-but-wrong value, so the human
 * check against the source document is the only control, and it is worth
 * nothing on a screen the reviewer has already left.
 */
export interface CompanyShellExtractionProvenance {
  /** The questionnaire's own filename, as the user chose it. */
  sourceFileName: string;
  /** Template columns the agent returned no value for. */
  missingColumns: string[];
}

/** Shared state across the bulk company-creation wizard's steps. */
export interface CompanyProvisionWizardState {
  /** Parsed/edited rows — seeded from Upload's parse result, mutated by Review's cell edits. */
  rows: CompanyShellRow[];
  /** CSV columns (header + requiredness legend) — same set Upload built, reused for the Review grid header. */
  columns: CompanyShellCsvColumn[];
  /** Client-side issues found while parsing the uploaded CSV. */
  parseErrors: CompanyShellCsvParseError[];
  /** Source CSV line for each entry in `rows`, using the same array index. */
  rowCsvLineNumbers: number[];
  /** Cells changed during review, keyed as `${rowIndex}:${csvHeader}`. */
  editedCellKeys: string[];
  /** The chosen configuration template, or null until Upload's selector sets one. */
  template: CompanyTemplateRef | null;
  /** Target MyPay API deployment for validate/commit invocations. */
  mypayEnvironment: MyPayEnvironment | null;
  /** The most recent `mode: "validate"` result, or null before the first Validate run. */
  validateResult: CompanyShellValidateResult | null;
  /**
   * True once a cell has been edited since the last validate run — the
   * displayed `validateResult` no longer describes the current rows. We keep
   * the (now stale) errors on screen so the user can still see which columns
   * to fix, but gate Commit off this flag until they re-run Validate.
   */
  validateStale: boolean;
  /** Monotonic revision of rows/environment/template used to reject stale validate responses. */
  validationInputRevision: number;
  /**
   * The `mode: "commit"` invocation's workflow id, set once Commit is
   * pressed on Review — lets the Results screen resume polling
   * `getInvocationStatus` after navigation, without re-invoking commit.
   * Track 1 only — Track 2's equivalent (`runId`) lives on `shellManager`
   * below, whose module doc explains why it has no such external-resume
   * path.
   */
  commitWorkflowId: string | null;
  /**
   * The raw uploaded CSV `File`, kept alongside the parsed `rows`/`columns`
   * so Track 2 (Step 16, LADR-039 / #440) can hand it to
   * `CompanyShellWorkloadsManagerReturn.setPendingData` — Track 2's
   * multipart import needs the original file, not just the client-parsed
   * rows. Unused by Track 1, which only ever sends parsed `rows` inline.
   */
  file: File | null;
  /**
   * Set when `rows` were extracted from a questionnaire, null when they were
   * read from a CSV. Written in the same `updateState` call as `rows`, so the
   * notice can never describe a batch other than the one on screen.
   */
  extraction: CompanyShellExtractionProvenance | null;
}

export const INITIAL_WIZARD_STATE: CompanyProvisionWizardState = {
  rows: [],
  columns: [],
  parseErrors: [],
  rowCsvLineNumbers: [],
  editedCellKeys: [],
  template: null,
  mypayEnvironment: null,
  validateResult: null,
  validateStale: false,
  validationInputRevision: 0,
  commitWorkflowId: null,
  file: null,
  extraction: null,
};

export interface CompanyProvisionWizardContextValue {
  state: CompanyProvisionWizardState;
  /** Merge a partial update into the current wizard state. */
  updateState: (partial: Partial<CompanyProvisionWizardState>) => void;
  /** True while an attempted in-app navigation is awaiting confirmation. */
  isNavigationBlocked?: boolean;
  /**
   * The Track 2 (Slice 5 workloads) manager instance, or `null` on Track 1.
   * Instantiated once at the Provider level (see
   * `BulkCreateCompanyWizardProvider`) so the same instance — and its
   * `runId`/`runStatus` — survives Review→Results navigation. Every screen
   * switches on this field: `null` renders the Track 1 UI (the existing
   * `useValidateCompanyShellProvision`/`useCommitCompanyShellProvision`
   * hooks), non-null renders Track 2's.
   */
  shellManager: CompanyShellWorkloadsManagerReturn | null;
}

export const CompanyProvisionWizardContext = createContext<
  CompanyProvisionWizardContextValue | undefined
>(undefined);
